import React from 'react';
import { PageHeader } from '../../components/page-headers/page-headers';
import { useAuth } from '../../context/auth.context';
import { Main } from '../styled';
import { ProjectDetailsWrapper } from './style';
import { useHistory } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { Card, message, Skeleton, Space, Tag, Typography } from 'antd';
import useApiFetch from '../../hooks/useApiFetch';
import { GettingStartedBanner } from '../../components/banners/Banners';
import Created from '../../components/commons/table-views/created';
import { DateAndTimeFormat } from '../../config/config';

export default function GettingStarted() {
    const { currentUser, checkUserAccessToResource } = useAuth();
    const history = useHistory();
    const { data: taskList, loading: taskListLoading } = useApiFetch(`/api/platform-tasks`, (data) => data.data);

    const tasks = taskList || [];
    const completedCount = tasks.filter((task) => task.completed).length;

    const openTask = (task) => {
        if (!hasAccessToTask(task)) {
            return message.info(`You don't have access to perform this task`);
        }
        if (!task.navigationPath) {
            return message.info('Setup for this step is not available yet');
        }
        return history.push(task.navigationPath);
    };

    const hasAccessToTask = (task) => {
        // forAdmin tasks are filtered server-side (see get-tasks.ts) — a non-admin never receives
        // them, so receiving one is itself the permission grant. Don't re-check it here.
        if (task?.forAdmin || !task?.requiredResource?.name) {
            return true;
        }
        return checkUserAccessToResource({
            resourceName: task.requiredResource.name,
            action: task.requiredActions?.[0],
        });
    };

    return (
        <ProjectDetailsWrapper>
            <PageHeader
                className="getting-started-header"
                routes={[
                    {
                        path: '/admin/overview',
                        icon: 'home',
                    },
                    {
                        breadcrumbName: 'Getting Started',
                        path: '/admin/getting-started',
                    },
                ]}
                title={`Hello, ${currentUser?.firstName} 👋`}
            />

            <Main className="getting-started-main">
                <GettingStartedBanner completedCount={completedCount} totalCount={tasks.length} />
                {taskListLoading
                    ? [1, 2, 3, 4, 5, 6].map((index) => (
                          <Card hoverable key={index} style={{ marginBottom: '20px' }}>
                              <Skeleton active paragraph={{ rows: 1 }} title />
                          </Card>
                      ))
                    : tasks.map((task, index) => {
                          const hasAccess = hasAccessToTask(task);

                          return (
                              <Card
                                  onClick={() => openTask(task)}
                                  title={
                                      <Space size="small">
                                          <span>
                                              {index + 1}. {task.taskName}
                                          </span>
                                          {task.completed && <Tag color="success">Done</Tag>}
                                      </Space>
                                  }
                                  hoverable
                                  key={task.taskId || task.title}
                                  style={{
                                      cursor: hasAccess ? 'pointer' : 'not-allowed',
                                      marginBottom: '20px',
                                  }}
                              >
                                  <div className="state-single">
                                      <div className={task.completed ? 'color-success' : 'color-gray'}>
                                          <span
                                              className="task-status-icon"
                                              title={
                                                  !hasAccess
                                                      ? `You don't have access to perform this task`
                                                      : task.completed
                                                      ? 'Completed'
                                                      : 'Pending'
                                              }
                                          >
                                              <FeatherIcon
                                                  icon={!hasAccess ? 'lock' : task.completed ? 'check-circle' : 'circle'}
                                                  size={18}
                                              />
                                          </span>
                                      </div>
                                      <div>
                                          <p>{task.description}</p>
                                          {task?.completedBy && (
                                              <Created
                                                  DateAndTimeFormat={DateAndTimeFormat}
                                                  createdAt={task.completedAt}
                                                  createdBy={task.completedBy}
                                              />
                                          )}
                                      </div>
                                  </div>
                              </Card>
                          );
                      })}
                {!taskListLoading && tasks.length === 0 && (
                    <Card>
                        <Typography.Text type="secondary">No setup tasks available right now.</Typography.Text>
                    </Card>
                )}
            </Main>
        </ProjectDetailsWrapper>
    );
}
